import { useMemo } from 'react';

import { Close, Search } from '@mui/icons-material';
import { Box, Button, Checkbox, FormControlLabel, FormGroup, InputAdornment, TextField, Typography } from '@mui/material';
import { useFormikContext } from 'formik';

import { PermissoesModulosChip } from 'pages/cadastro/permissoes/components/form/modulos/PermissoesModulosChip';
import { PermissoesProps } from 'pages/cadastro/permissoes/types';
import { getEnabledCount, getModuleStatus } from 'pages/cadastro/permissoes/utils';

export type PermissoesModulosOpcoes = {
    selectedModuleId: number;
    permSearch: string;
    setPermSearch: (v: string) => void;
};

export function PermissoesModulosOpcoes({ selectedModuleId, permSearch, setPermSearch }: PermissoesModulosOpcoes) {
    const { setFieldValue, values } = useFormikContext<PermissoesProps>();
    const selectedModuleIndex = values.grupoUsuarioRecursoPermissoes.findIndex((m) => m.idRecurso === selectedModuleId);
    const selectedModule = values.grupoUsuarioRecursoPermissoes[selectedModuleIndex];
    const enabledInCurrent = selectedModule ? getEnabledCount(selectedModule) : 0;
    const totalInCurrent = selectedModule ? selectedModule.permissoes.length : 0;
    const { status } = getModuleStatus(enabledInCurrent, totalInCurrent);

    /**
     * Altera o valor de uma permissão específica
     */
    const handleChange = (checked: boolean, moduleIndex: number, permIndex: number) => {
        setFieldValue(`grupoUsuarioRecursoPermissoes[${moduleIndex}].permissoes[${permIndex}].fgPermiteAcesso`, checked);
    };

    /**
     * Seta todas as permissões do módulo para o valor informado
     */
    const handleSetAll = (val: boolean) => {
        setFieldValue(
            `grupoUsuarioRecursoPermissoes[${selectedModuleIndex}].permissoes`,
            selectedModule.permissoes.map((p) => ({ ...p, fgPermiteAcesso: val })),
        );
    };

    /**
     * Filtra as permissões do módulo pelo campo de busca
     */
    const filteredPerms = useMemo(
        () => selectedModule.permissoes.filter((p) => p.dsPermissao.toLowerCase().includes(permSearch.toLowerCase())),
        [selectedModule, permSearch],
    );

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box
                sx={{
                    px: 2,
                    py: { xs: 1.5, md: 0 },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5,
                    flexShrink: 0,
                    minHeight: 62,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{selectedModule.text}</Typography>

                    <PermissoesModulosChip label={`${enabledInCurrent}/${totalInCurrent}`} status={status} />
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', md: 'auto' } }}>
                    <Button size="small" variant="outlined" color="primary" onClick={() => handleSetAll(false)} sx={{ flex: { xs: 1, md: 'none' } }}>
                        Remover tudo
                    </Button>

                    <Button size="small" variant="contained" color="primary" onClick={() => handleSetAll(true)} sx={{ flex: { xs: 1, md: 'none' } }}>
                        Marcar tudo
                    </Button>
                </Box>
            </Box>

            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder={`Buscar em ${selectedModule.text}...`}
                    value={permSearch}
                    onChange={(e) => setPermSearch(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                            endAdornment: permSearch ? (
                                <InputAdornment position="end">
                                    <Close sx={{ fontSize: 16, cursor: 'pointer', color: 'text.disabled' }} onClick={() => setPermSearch('')} />
                                </InputAdornment>
                            ) : undefined,
                        },
                    }}
                />
            </Box>

            {filteredPerms.length === 0 && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.disabled">
                        Nenhuma permissão encontrada
                    </Typography>
                </Box>
            )}

            {filteredPerms.length !== 0 && (
                <FormGroup
                    sx={{
                        px: 3,
                        py: 2,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' },
                        gap: 1,
                        overflowY: 'auto',
                        alignContent: 'start',
                    }}
                >
                    {filteredPerms.map((permissao) => {
                        const permIndex = selectedModule.permissoes.findIndex((p) => p.idPermissao === permissao.idPermissao);

                        return (
                            <FormControlLabel
                                key={permissao.idPermissao}
                                label={permissao.dsPermissao}
                                control={
                                    <Checkbox
                                        checked={!!permissao.fgPermiteAcesso}
                                        onChange={(e) => handleChange(e.target.checked, selectedModuleIndex, permIndex)}
                                        size="small"
                                    />
                                }
                            />
                        );
                    })}
                </FormGroup>
            )}
        </Box>
    );
}
